<template>
  <div class="general-container">
    <h1>Servicios de Divulgación y Educación Ambiental</h1>
    <p class="intro-texto">Trabajamos en tres líneas que se complementan: aprender sobre el terreno, regenerar el entorno en comunidad y crear las herramientas digitales que lo hacen posible.</p>

    <section class="linea" v-for="linea in lineas" :key="linea.title">
      <h2>{{ linea.title }}</h2>
      <p class="linea-texto">{{ linea.content }}</p>

      <div class="servicios-grid">
        <router-link
          v-for="servicio in linea.servicios"
          :key="servicio.title"
          :to="servicio.link"
          class="card"
          :class="{ 'card-ancha': linea.servicios.length === 1 }"
        >
          <img :src="servicio.image" :alt="servicio.title" :title="servicio.title" loading="lazy">
          <div>
            <h3>{{ servicio.title }}</h3>
            <p>{{ servicio.content }}</p>
          </div>
        </router-link>
      </div>

      <p class="hecho-en">
        <strong>Lo hemos hecho en:</strong>
        <template v-for="(proyecto, index) in linea.proyectos" :key="proyecto.title">
          {{ ' ' }}<router-link :to="proyecto.link">{{ proyecto.title }}</router-link>{{ index < linea.proyectos.length - 1 ? ' ·' : '' }}
        </template>
      </p>
    </section>

    <section class="franja-recursos">
      <div>
        <h2>Material didáctico gratuito</h2>
        <p>Fichas didácticas, láminas imprimibles y guías de campo para apoyar el aprendizaje en cualquier entorno.</p>
      </div>
      <router-link to="/servicios/descargas" class="btn-servicio">Ver recursos y descargas</router-link>
    </section>
  </div>
</template>

<script setup>
import { useHead } from '@vueuse/head'

const pageUrl = 'https://www.geobizi.com/servicios'
const ogImage = 'https://www.geobizi.com/imagenes/og-geobizi.jpg'

useHead({
  title: 'Servicios de Divulgación y Educación Ambiental | Geobizi',
  meta: [
    { name: 'description', content: 'Rutas, talleres y formación técnica, proyectos comunitarios de biorregeneración y soluciones digitales en Bizkaia, además de material didáctico gratuito.' },
    { name: 'robots', content: 'index, follow' },
    { name: 'author', content: 'Geobizi' },
    { name: 'theme-color', content: '#0b8a4c' },
    { name: 'language', content: 'es' },
    { property: 'og:title', content: 'Servicios de Divulgación y Educación Ambiental | Geobizi' },
    { property: 'og:description', content: 'Experiencias didácticas, proyectos comunitarios de biorregeneración y soluciones digitales para centros educativos, instituciones, empresas y familias.' },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: pageUrl },
    { property: 'og:image', content: ogImage }
  ],
  link: [
    { rel: 'canonical', href: pageUrl },
    { rel: 'image_src', href: ogImage },
    { rel: 'apple-touch-icon', href: '/apple-touch-icon.png', sizes: '180x180' }
  ],
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebPage",
            "url": pageUrl,
            "name": "Servicios de Divulgación y Educación Ambiental | Geobizi",
            "description": "Experiencias didácticas, proyectos comunitarios de biorregeneración y soluciones digitales en Bizkaia.",
            "inLanguage": "es",
            "isPartOf": { "@id": "https://www.geobizi.com/#website" },
            "image": { "@type": "ImageObject", "url": ogImage, "width": 1200, "height": 630 }
          }
        ]
      })
    }
  ]
})

const lineas = [
  {
    title: 'Experiencias didácticas y divulgación',
    content: 'Rutas, talleres y formación para aprender sobre el terreno. Para centros educativos, ayuntamientos, familias y equipos profesionales.',
    servicios: [
      {
        title: 'Rutas divulgativas',
        content: 'Rutas guiadas por la naturaleza, geología y botánica de Bizkaia. Experiencias cercanas y adaptadas a cada grupo.',
        link: '/servicios/rutas',
        image: '/imagenes/servicios/rutas/rutas.avif',
      },
      {
        title: 'Talleres y actividades',
        content: 'Talleres prácticos y educativos para colegios, eventos y proyectos locales. ¡Aprende experimentando!',
        link: '/servicios/actividades',
        image: '/imagenes/servicios/actividades/actividades.avif',
      },
      {
        title: 'Formación profesional y técnica',
        content: 'Ecoturismo, huertos urbanos y gestión del ocio, además de comunicación, trabajo en equipo y uso práctico de la IA.',
        link: '/servicios/formacion',
        image: '/imagenes/servicios/formacion/formacion.avif',
      },
    ],
    proyectos: [
      { title: 'Semana Verde de Getxo', link: '/detalle-aste-berdea' },
      { title: 'Sopela Kosta Fest', link: '/detalle-sopela-kosta-fest' },
      { title: 'Mungia y la Red de Ekoetxeas', link: '/proyectos' },
    ],
  },
  {
    title: 'Proyectos comunitarios y biorregeneración',
    content: 'Proyectos colaborativos para recuperar y cuidar los espacios naturales junto a quienes viven en ellos. Para ayuntamientos, centros educativos, asociaciones y empresas.',
    servicios: [
      {
        title: 'Sensibilización y biorregeneración',
        content: 'Asesoramiento, diseño de espacios verdes, bombas de semillas y procesos participativos, desde acciones de un día hasta programas que acompañan a lo largo del tiempo.',
        link: '/servicios/sensibilizacion',
        image: '/imagenes/servicios/sensibilizacion/sensibilizacion.avif',
      },
    ],
    proyectos: [
      { title: 'Zalla Natura', link: '/detalle-zalla-natura' },
    ],
  },
  {
    title: 'Soluciones digitales (Geobizi<TIK>)',
    content: 'Tecnología eficiente para divulgar mejor y simplificar la gestión. Para ayuntamientos, entidades, festivales y proyectos educativos.',
    servicios: [
      {
        title: 'Geobizi<TIK>',
        content: 'Webs a medida, sistemas de reservas con gestión de aforos, accesibilidad digital, yincanas digitales y recursos educativos interactivos.',
        link: '/servicios/digital-sostenible',
        image: '/imagenes/servicios/digital/digital.avif',
      },
    ],
    proyectos: [
      { title: 'Flysch Digital', link: '/detalle-flysch' },
      { title: 'Fichas Etnobotánicas de Enkarterri', link: '/fitxas-etnobotanicas-enkarterri' },
    ],
  },
]
</script>

<style scoped>
.general-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  padding-top: 7rem;
  padding-left: 2rem;
  padding-right: 2rem;
  padding-bottom: 2rem;
}

.general-container > h1 {
  margin-left: 0;
  margin-right: 0;
}

.intro-texto {
  font-size: 1.1rem;
  max-width: 50rem;
  margin: 0 0 2rem;
}

.linea {
  margin-bottom: 3.5rem;
}

.linea > h2 {
  font-size: 1.5rem;
  border-bottom: 2px solid var(--shoftgreen);
  padding-bottom: 0.5rem;
  margin: 0 0 1rem;
}

.linea-texto {
  margin: 0 0 1.5rem;
  max-width: 50rem;
}

.servicios-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
  gap: 1.5rem;
}

.card {
  display: block;
  padding: 1rem;
  background-color: var(--megashoftgreen);
  border: 1px solid var(--shoftgreen);
  border-radius: 0.5rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  font-weight: normal;
  font-style: normal;
  transition: transform 0.3s, box-shadow 0.3s;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.card img {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 0.5rem;
}

.card h3 {
  margin: 0.75rem 0 0.25rem;
  color: var(--darkgreen);
}

.card p {
  margin: 0;
  color: var(--darkgrey);
}

/* Líneas con un solo servicio: imagen a un lado para no estirarla a todo el ancho */
.card-ancha {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.card-ancha img {
  width: 280px;
  flex-shrink: 0;
}

.card-ancha h3 {
  margin-top: 0;
}

.hecho-en {
  margin: 1rem 0 0;
}

.franja-recursos {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 1.5rem 2rem;
  background-color: var(--supershoftgreen);
  border-radius: 0.5rem;
}

.franja-recursos h2 {
  margin: 0 0 0.25rem;
  color: var(--darkgreen);
}

.franja-recursos p {
  margin: 0;
}

.franja-recursos .btn-servicio {
  flex-shrink: 0;
  font-style: normal;
}

@media (max-width: 750px) {
  .general-container {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .card-ancha,
  .franja-recursos {
    flex-direction: column;
    align-items: flex-start;
  }

  .card-ancha {
    gap: 0;
  }

  .card-ancha img {
    width: 100%;
  }

  .card-ancha h3 {
    margin-top: 0.75rem;
  }
}
</style>
